import { useEffect, useCallback } from 'react';

// Keyboard shortcut mappings
const SHORTCUTS = {
    // Navigation shortcuts
    'ctrl+k': 'search',
    'ctrl+/': 'help',
    'ctrl+shift+p': 'patients',
    'ctrl+shift+a': 'appointments',
    'ctrl+shift+s': 'staff',
    'ctrl+shift+i': 'inventory',
    'ctrl+shift+r': 'reports',
    'ctrl+shift+u': 'settings',
    
    // Action shortcuts
    'ctrl+n': 'new',
    'ctrl+s': 'save',
    'ctrl+z': 'undo',
    'ctrl+y': 'redo',
    'ctrl+f': 'find',
    'ctrl+h': 'find-replace',
    'ctrl+a': 'select-all',
    'ctrl+c': 'copy',
    'ctrl+v': 'paste',
    'ctrl+x': 'cut',
    
    // Quick actions
    'ctrl+shift+n': 'new-patient',
    'ctrl+shift+c': 'new-appointment',
    'ctrl+shift+t': 'new-staff',
    'ctrl+shift+m': 'new-inventory',
    
    // Dashboard shortcuts
    'ctrl+1': 'dashboard',
    'ctrl+2': 'doctor-dashboard',
    'ctrl+3': 'nurse-dashboard',
    'ctrl+4': 'receptionist-dashboard',
    'ctrl+5': 'pharmacist-dashboard',
    
    // Utility shortcuts
    'ctrl+shift+d': 'dark-mode',
    'ctrl+shift+l': 'logout',
    'ctrl+shift+h': 'home',
    'escape': 'close-modal'
};

export const useKeyboardShortcuts = (onAction) => {

    const handleKeyDown = useCallback((event) => {
        // Don't trigger shortcuts when typing in inputs
        if (
            event.target.tagName === 'INPUT' ||
            event.target.tagName === 'TEXTAREA' ||
            event.target.contentEditable === 'true' ||
            event.target.closest('[contenteditable="true"]')
        ) {
            return;
        }

        // Build shortcut key
        const keys = [];
        if (event.ctrlKey) keys.push('ctrl');
        if (event.shiftKey) keys.push('shift');
        if (event.altKey) keys.push('alt');
        if (event.metaKey) keys.push('meta');
        
        // Add the main key
        const mainKey = event.key.toLowerCase();
        keys.push(mainKey);
        
        const shortcut = keys.join('+');
        
        // Check if this is a known shortcut
        const action = SHORTCUTS[shortcut];
        if (action) {
            event.preventDefault();
            event.stopPropagation();
            
            // Execute the action
            executeAction(action, { event, onAction });
        }
    }, [onAction]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
};

const executeAction = (action, { event, onAction }) => {
    switch (action) {
        case 'search':
            // Focus search input or open search modal
            const searchInput = document.querySelector('input[type="search"], input[placeholder*="search" i]');
            if (searchInput) {
                searchInput.focus();
            } else if (onAction) {
                onAction('open-search');
            }
            break;
            
        case 'help':
            if (onAction) {
                onAction('open-help');
            }
            break;
            
        case 'patients':
            onAction('navigate', 'patients');
            break;
            
        case 'appointments':
            onAction('navigate', 'appointments');
            break;
            
        case 'staff':
            onAction('navigate', 'staff');
            break;
            
        case 'inventory':
            onAction('navigate', 'inventory');
            break;
            
        case 'reports':
            onAction('navigate', 'reports');
            break;
            
        case 'settings':
            onAction('navigate', 'settings');
            break;
            
        case 'new':
            if (onAction) {
                onAction('new-item');
            }
            break;
            
        case 'new-patient':
            onAction('navigate', 'add-patient');
            break;
            
        case 'new-appointment':
            onAction('navigate', 'add-appointment');
            break;
            
        case 'new-staff':
            onAction('navigate', 'add-staff');
            break;
            
        case 'new-inventory':
            onAction('navigate', 'add-inventory-item');
            break;
            
        case 'dashboard':
            onAction('navigate', 'dashboard');
            break;
            
        case 'doctor-dashboard':
            onAction('navigate', 'dashboard');
            break;
            
        case 'nurse-dashboard':
            onAction('navigate', 'dashboard');
            break;
            
        case 'receptionist-dashboard':
            onAction('navigate', 'dashboard');
            break;
            
        case 'pharmacist-dashboard':
            onAction('navigate', 'dashboard');
            break;
            
        case 'dark-mode':
            if (onAction) {
                onAction('toggle-theme');
            }
            break;
            
        case 'logout':
            if (onAction) {
                onAction('logout');
            }
            break;
            
        case 'home':
            onAction('navigate', 'dashboard');
            break;
            
        case 'close-modal':
            // Close any open modals
            const modals = document.querySelectorAll('[role="dialog"], .modal');
            modals.forEach(modal => {
                const closeButton = modal.querySelector('[data-dismiss="modal"], .close-button');
                if (closeButton) {
                    closeButton.click();
                }
            });
            break;
            
        default:
            if (onAction) {
                onAction(action);
            }
    }
};

// Hook for specific component shortcuts
export const useComponentShortcuts = (shortcuts, dependencies = []) => {
    const handleKeyDown = useCallback((event) => {
        // Don't trigger shortcuts when typing in inputs
        if (
            event.target.tagName === 'INPUT' ||
            event.target.tagName === 'TEXTAREA' ||
            event.target.contentEditable === 'true' ||
            event.target.closest('[contenteditable="true"]')
        ) {
            return;
        }

        const keys = [];
        if (event.ctrlKey) keys.push('ctrl');
        if (event.shiftKey) keys.push('shift');
        if (event.altKey) keys.push('alt');
        if (event.metaKey) keys.push('meta');
        
        const mainKey = event.key.toLowerCase();
        keys.push(mainKey);
        
        const shortcut = keys.join('+');
        
        if (shortcuts[shortcut]) {
            event.preventDefault();
            event.stopPropagation();
            shortcuts[shortcut](event);
        }
    }, dependencies);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
};

// Keyboard shortcut help component
export const KeyboardShortcutsHelp = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const shortcutGroups = [
        {
            title: 'Navigation',
            shortcuts: [
                { keys: 'Ctrl + K', description: 'Search' },
                { keys: 'Ctrl + /', description: 'Show help' },
                { keys: 'Ctrl + Shift + P', description: 'Go to Patients' },
                { keys: 'Ctrl + Shift + A', description: 'Go to Appointments' },
                { keys: 'Ctrl + Shift + S', description: 'Go to Staff' },
                { keys: 'Ctrl + Shift + I', description: 'Go to Inventory' },
                { keys: 'Ctrl + Shift + R', description: 'Go to Reports' },
                { keys: 'Ctrl + Shift + U', description: 'Go to Settings' }
            ]
        },
        {
            title: 'Quick Actions',
            shortcuts: [
                { keys: 'Ctrl + N', description: 'New item' },
                { keys: 'Ctrl + Shift + N', description: 'New Patient' },
                { keys: 'Ctrl + Shift + C', description: 'New Appointment' },
                { keys: 'Ctrl + Shift + T', description: 'New Staff' },
                { keys: 'Ctrl + Shift + M', description: 'New Inventory' }
            ]
        },
        {
            title: 'Dashboards',
            shortcuts: [
                { keys: 'Ctrl + 1', description: 'Main Dashboard' },
                { keys: 'Ctrl + 2', description: 'Doctor Dashboard' },
                { keys: 'Ctrl + 3', description: 'Nurse Dashboard' },
                { keys: 'Ctrl + 4', description: 'Receptionist Dashboard' },
                { keys: 'Ctrl + 5', description: 'Pharmacist Dashboard' }
            ]
        },
        {
            title: 'Utilities',
            shortcuts: [
                { keys: 'Ctrl + Shift + D', description: 'Toggle Dark Mode' },
                { keys: 'Ctrl + Shift + L', description: 'Logout' },
                { keys: 'Ctrl + Shift + H', description: 'Go to Home' },
                { keys: 'Escape', description: 'Close Modal' }
            ]
        }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        Keyboard Shortcuts
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {shortcutGroups.map((group, index) => (
                        <div key={index}>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                                {group.title}
                            </h3>
                            <div className="space-y-2">
                                {group.shortcuts.map((shortcut, idx) => (
                                    <div key={idx} className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600 dark:text-gray-300">
                                            {shortcut.description}
                                        </span>
                                        <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded text-xs font-mono">
                                            {shortcut.keys}
                                        </kbd>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Press <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">Escape</kbd> to close this help.
                    </p>
                </div>
            </div>
        </div>
    );
};
